import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { eventsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Calendar, MapPin, Clock, ExternalLink, Users } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export const EventDetailPage = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await eventsAPI.getById(id);
        setEvent(res.data);
      } catch (err) {
        console.error('Error fetching event:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id]);

  if (loading) {
    return (
      <div className="container-wide py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-12 bg-muted rounded w-1/2" />
          <div className="h-4 bg-muted rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container-wide py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Event not found</h1>
        <Link to="/events">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Events
          </Button>
        </Link>
      </div>
    );
  }

  const eventDate = parseISO(event.date);

  return (
    <div data-testid="event-detail-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <Link to="/events" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Events
          </Link>
          
          <div className="flex items-start gap-6">
            <div className="text-center min-w-[80px] border border-border p-4 rounded-sm">
              <div className="text-3xl font-bold">{format(eventDate, 'd')}</div>
              <div className="text-sm text-muted-foreground uppercase">{format(eventDate, 'MMM')}</div>
              <div className="text-xs text-muted-foreground">{format(eventDate, 'yyyy')}</div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="badge-category">{event.event_type}</span>
                {event.is_virtual && (
                  <span className="text-xs text-muted-foreground">Virtual Event</span>
                )}
                {event.featured && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">Featured</span>
                )}
              </div>
              <h1 className="text-display text-3xl lg:text-4xl mb-3">{event.title}</h1>
              <p className="text-muted-foreground max-w-2xl">{event.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section>
        <div className="container-wide py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* About */}
              <div>
                <h2 className="text-heading text-xl mb-4">About this Event</h2>
                <p className="text-muted-foreground leading-relaxed">{event.description}</p>
              </div>

              {/* Tags */}
              {event.tags && event.tags.length > 0 && (
                <div>
                  <h2 className="text-heading text-xl mb-4">Topics</h2>
                  <div className="flex flex-wrap gap-2">
                    {event.tags.map((tag, i) => (
                      <span key={i} className="badge-category">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="font-semibold">{format(eventDate, 'EEEE, MMMM d, yyyy')}</div>
                      <div className="text-xs text-muted-foreground">Date</div>
                    </div>
                  </div>
                  {event.time && (
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="font-semibold">{event.time}</div>
                        <div className="text-xs text-muted-foreground">Time</div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="font-semibold">{event.location}</div>
                      <div className="text-xs text-muted-foreground">Location</div>
                    </div>
                  </div>
                  {event.organizer && (
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="font-semibold">{event.organizer}</div>
                        <div className="text-xs text-muted-foreground">Organizer</div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {event.registration_url ? (
                <a href={event.registration_url} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full" size="lg">
                    Register Now
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              ) : (
                <Button className="w-full" size="lg">
                  RSVP
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
